import React, { ChangeEvent, FC, useContext, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Check, Delete, Edit } from '@mui/icons-material';
import GMBService from '../../../services/GMBService';
import { IGMBComment } from '../IGMBComment';
import { useTranslation } from 'react-i18next';
import CommentAiService from '../../../services/CommentAiService';
import { CommunityManagement } from '../../../shared/interfaces/ICommunityManagement';
import { AuthContext } from '../../../providers/AuthProvider';
import { constants } from '../../../constants/constants';

interface Props {
  reviewerName: string;
  reviewId: string;
  locationId: string;
  comment: string;
  input: string;
  reviewReply: IGMBComment | null;
  communityManagementSettings: CommunityManagement;
}

const CommentContainer = styled.div<{ reviewReply: IGMBComment | null }>`
  display: flex;
  flex-direction: column;
  background-color: ${(props) =>
    props.reviewReply !== null
      ? props.theme.palette.background.default
      : props.theme.palette.background.paper};
  padding: ${(props) => (props.reviewReply !== null ? props.theme.spacing(2) : '0')};
  border-radius: ${(props) => props.theme.shape.borderRadius}px;
`;

const GMBReviewReply: FC<Props> = ({
  reviewerName,
  comment,
  reviewReply,
  reviewId,
  locationId,
  communityManagementSettings,
  input,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [reply, setReply] = useState<IGMBComment | null>(reviewReply);
  const [replied, setReplied] = useState<boolean>(false);
  const [deleted, setDeleted] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const commentRef = useRef<HTMLInputElement | null>(null);

  const handlePostReply = async () => {
    if (!reply) return;
    await GMBService.replyToReview(locationId, reviewId, reply.comment, setLoading);
    setReplied(true);
    setEditing(false);
  };

  const handleDeleteReply = async () => {
    await GMBService.deleteReviewReply(locationId, reviewId, setLoading);
    setDeleted(true);
    setEditing(false);
  };

  const handleGenerateReply = async () => {
    const data = await CommentAiService.generateComment(
      communityManagementSettings.languagePreference,
      constants.tonality[communityManagementSettings.tonality.preference],
      communityManagementSettings.pronoun.preference === 0,
      comment,
      setLoading
    );

    if (!data) return;
    setReply({
      comment:
        communityManagementSettings.salutationPrefix +
        (communityManagementSettings.useUsername ? ' ' + reviewerName : '') +
        ',\n\n' +
        data +
        '\n\n' +
        communityManagementSettings.greeting,
    });
  };

  return (
    <>
      {reviewReply !== null ? (
        <CommentContainer reviewReply={reviewReply}>
          {!deleted ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
              <Box sx={{ flex: 1, whiteSpace: 'pre-line' }}>
                {editing ? (
                  <TextField
                    label="Kommentar"
                    inputRef={commentRef}
                    multiline
                    rows={8}
                    fullWidth
                    InputLabelProps={{ shrink: !!reply?.comment }}
                    value={reply?.comment}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setReply((prevState) => ({ ...prevState, comment: event.target.value }))
                    }
                  />
                ) : (
                  <Typography variant="body2">{reviewReply.comment}</Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  ml: theme.spacing(2),
                }}
              >
                {editing && (
                  <Box
                    sx={{
                      display: 'flex',
                      my: theme.spacing(2),
                    }}
                  >
                    {hasRole('can-use-ai') && (
                      <Button
                        disabled={loading || !input}
                        variant="contained"
                        onClick={async () => await handleGenerateReply()}
                        sx={{ mr: theme.spacing(1) }}
                      >
                        {t('generate')}
                      </Button>
                    )}
                    <Button
                      disabled={loading || !reply!.comment}
                      variant="contained"
                      onClick={async () => await handlePostReply()}
                      sx={{ ml: 1 }}
                    >
                      {t('to_comment')}
                    </Button>
                    <Button
                      disabled={loading}
                      variant="contained"
                      color="warning"
                      onClick={() => {
                        setReply({ comment: '' });
                      }}
                      sx={{ ml: 1 }}
                    >
                      {t('reset')}
                    </Button>
                    {loading && (
                      <CircularProgress
                        sx={{ ml: theme.spacing(1) }}
                        size={24}
                      />
                    )}
                  </Box>
                )}
                {!editing && (
                  <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    {loading && (
                      <CircularProgress
                        sx={{ ml: theme.spacing(1) }}
                        size={24}
                      />
                    )}
                    <IconButton
                      disabled={loading}
                      onClick={async () => {
                        if (editing) {
                          setEditing(false);
                        } else {
                          setEditing(true);
                        }
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      disabled={loading}
                      color="error"
                      onClick={async () => await handleDeleteReply()}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: theme.spacing(4) }}>
              <Check color="primary" />
              <Typography
                variant="body1"
                sx={{ ml: theme.spacing(1) }}
              >
                {/* TODO: übersetzen */}
                Die Antwort wurde gelöscht.
              </Typography>
            </Box>
          )}
        </CommentContainer>
      ) : (
        <CommentContainer reviewReply={reviewReply}>
          {!replied ? (
            <>
              <TextField
                label="Kommentar"
                inputRef={commentRef}
                multiline
                rows={8}
                InputLabelProps={{ shrink: !!reply?.comment }}
                value={reply?.comment}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setReply((prevState) => ({ ...prevState, comment: event.target.value }))
                }
              />
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                {hasRole('can-use-ai') && (
                  <Button
                    disabled={loading || !comment}
                    variant="contained"
                    onClick={async () => await handleGenerateReply()}
                    sx={{ mr: theme.spacing(1) }}
                  >
                    {t('generate')}
                  </Button>
                )}
                <Button
                  disabled={loading}
                  variant="contained"
                  onClick={async () => await handlePostReply()}
                >
                  {/* TODO: übersetzen */}
                  Kommentieren
                </Button>
                <Button
                  disabled={loading}
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    if (commentRef.current) {
                      commentRef.current.value = '';
                    }
                    setReply(null);
                  }}
                  sx={{ ml: 1 }}
                >
                  {t('reset')}
                </Button>
                {loading && (
                  <CircularProgress
                    sx={{ ml: theme.spacing(1) }}
                    size={24}
                  />
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: theme.spacing(4) }}>
              <Check color="primary" />
              <Typography
                variant="body1"
                sx={{ ml: theme.spacing(1) }}
              >
                {/* TODO: übersetzen */}
                Ihre Antwort wurde veröffentlicht!
              </Typography>
            </Box>
          )}
        </CommentContainer>
      )}
    </>
  );
};

export default GMBReviewReply;
